import React, {Component, ReactElement, ReactNode} from 'react'
import {
    AVAILABLE_OR_NOT,
    CANCEL_REASON,
    CASH_PAYMENT_STATUS,
    FACILITY_RESERVATION_CLASSIFICATION,
    MESSAGE_TYPE,
    PAYMENT_TYPE,
    RESERVATION_STATUS,
    RESERVATION_TYPE,
} from '../../../CodeMaster'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'
import {StyledTableCaption, TableRowName} from '../../../styledComponents/adminPage/Table'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../../../components/shared/hoc/withAdminPageTemplate'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {BackLink, DeleteLink} from '../../../styledComponents/adminPage/Links'
import {Box, TableBody, TableCell, TableRow} from '@mui/material'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {HKConstants} from '../../../HKConstants'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {Messages} from '../../../Messages'
import NatFacilityReserveRepo from './repository/NatFacilityReserveRepo'
import {formatNumberWithCommas} from '../../../formatters/formatNumberWithCommas'
import {NatGetFacilityReserve, Options, SelectedUserList} from './repository/NatGetFacilityReserve'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {NatDeleteFacilityReserve} from './repository/NatDeleteFacilityReserve'
import {ConditionTableFixed} from '../../../styledComponents/adminPage/ConditionTable'
import styled from 'styled-components'
import {formatDateTimeForUpdateDate} from '../../../formatters/formatDate'
import {Location} from 'react-router-dom'

const SELECTED_CONDOMINIUM_CD: string = 'selectedCondominiumCd'

// キャンセル期間外
const RESERVE_CANCEL_WARNING_OUT_OF_TERM: string = '1'
// キャンセル不可
const RESERVE_CANCEL_WARNING_UNAVAILABLE: string = '2'
// キャンセル可
const RESERVE_CANCEL_WARNING_AVAILABLE: string = '0'

interface Props {
    facilityReserveRepo: NatFacilityReserveRepo
    facilityReserveId: string
    facilityId: string
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    facilityName: string
    facilityReserveCategory: string
    reserveType: string
    reserveStatus: string
    cancelReason: Nullable<string>
    reserveTitle: string
    stayAvailability: string
    stayCount: string
    startDate: string
    startTime: string
    endDate: string
    endTime: string
    selectedUserList: SelectedUserList[]
    options: Options[]
    facilityUseCharge: number
    paymentMethod: string
    cashPaymentStatus: string
    totalCharge: number
    selectedCondominiumCd: string
    userRidgeName: string
    userRoomNumber: string
    userFamilyName: string
    userFirstName: string
    adminFamilyName: string
    adminFirstName: string
    userRoleName: string
    reserveFamilyName: string
    reserveFirstName: string
    reserveRoleName: string
    reserveCancelWarningFlag: string
    version: number
    lastUpdaterFamilyName: string
    lastUpdaterFirstName: string
    updateDateTime: string
    isLoadCompleted: boolean
}


class NatAdminFacilityReservePage extends Component<Props & AdminPagePropsSet, States> {

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        const selectedCondominiumGroupId: Nullable<string> =
            SessionStorageHelper.getStringItemOrNull(SELECTED_CONDOMINIUM_CD)

        this.state = {
            facilityName: '',
            facilityReserveCategory: '',
            reserveType: '',
            reserveStatus: '',
            cancelReason: null,
            reserveTitle: '',
            stayAvailability: '',
            stayCount: '',
            startDate: '',
            startTime: '',
            endDate: '',
            endTime: '',
            selectedUserList: [],
            options: [],
            facilityUseCharge: 0,
            paymentMethod: '',
            cashPaymentStatus: '',
            totalCharge: 0,
            selectedCondominiumCd: String(selectedCondominiumGroupId),
            userRidgeName: '',
            userRoomNumber: '',
            userFamilyName: '',
            userFirstName: '',
            adminFamilyName: '',
            adminFirstName: '',
            userRoleName: '',
            reserveFamilyName: '',
            reserveFirstName: '',
            reserveRoleName: '',
            reserveCancelWarningFlag: RESERVE_CANCEL_WARNING_AVAILABLE,
            version: 0,
            lastUpdaterFamilyName: '',
            lastUpdaterFirstName: '',
            updateDateTime: '',
            isLoadCompleted: false,
        }
        this.backFacilityReserveListLinkClick = this.backFacilityReserveListLinkClick.bind(this)
        this.editFacilityReserveLinkClick = this.editFacilityReserveLinkClick.bind(this)
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this)
        this.handleCancelReserveOkClick = this.handleCancelReserveOkClick.bind(this)
        this.cancelReserve = this.cancelReserve.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatGetFacilityReserve> = await this.props.asyncCallFunction(
                this.props.facilityReserveRepo.getFacilityReserve.bind(
                    this.props.facilityReserveRepo,
                    this.state.selectedCondominiumCd,
                    this.props.facilityId,
                    this.props.facilityReserveId),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )

            if (response && response.details) {
                this.setState({
                    facilityName: response.details.facilityName,
                    facilityReserveCategory: response.details.facilityReserveCategory,
                    reserveType: response.details.reserveType,
                    reserveStatus: response.details.reserveStatus,
                    cancelReason: response.details.cancelReason,
                    reserveTitle: response.details.reservationTitle,
                    stayAvailability: response.details.stayAvailability,
                    startDate: response.details.startDate,
                    startTime: response.details.startTime,
                    endDate: response.details.endDate,
                    endTime: response.details.endTime,
                    stayCount: response.details.stayCount,
                    totalCharge: response.details.facilityUseCharge +
                        response.details.options.reduce((p, x) => p + x.charge, 0),
                    options: response.details.options,
                    facilityUseCharge: response.details.facilityUseCharge,
                    paymentMethod: response.details.paymentMethod,
                    cashPaymentStatus: response.details.cashPaymentStatus,
                    selectedUserList: response.details.selectedUserList,
                    userRidgeName: response.details.userRidgeName,
                    userRoomNumber: response.details.userRoomNumber,
                    userFamilyName: response.details.userFamilyName,
                    userFirstName: response.details.userFirstName,
                    adminFamilyName: response.details.adminFamilyName,
                    adminFirstName: response.details.adminFirstName,
                    userRoleName: response.details.userRoleName,
                    reserveFamilyName: response.details.reserveFamilyName,
                    reserveFirstName: response.details.reserveFirstName,
                    reserveRoleName: response.details.reserveRoleName,
                    reserveCancelWarningFlag: response.details.reserveCancelWarningFlag,
                    version: response.details.version,
                    lastUpdaterFamilyName: response.details.lastUpdaterFamilyName,
                    lastUpdaterFirstName: response.details.lastUpdaterFirstName,
                    updateDateTime: formatDateTimeForUpdateDate(response.details.updateDateTime),
                })

            }
            this.showInitialMessage()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
        this.props.eventEmitter.closeLoading()
    }

    backFacilityReserveListLinkClick(): void {
        this.props.navigate(`/admin/facilityReserveList`)
    }

    editFacilityReserveLinkClick(): void {
        this.props.navigate(
            `/admin/facilityReserveEdit/${this.props.facilityId}/${this.props.facilityReserveId}`,
            {
                state: {
                    version: this.state.version,
                },
            },
        )
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleCancelReserveOkClick(): Promise<void> {
        this.props.eventEmitter.closeDialog()
        await this.props.eventEmitter.showLoading()

        try {
            const ret: Nullable<NatDeleteFacilityReserve> = await this.props.asyncCallFunction(
                this.props.facilityReserveRepo.deleteFacilityReserve.bind(
                    this.props.facilityReserveRepo, this.props.facilityReserveId, this.state.version),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (ret && ret.requestError === null) {
                this.props.navigate(
                    `/admin/facilityReserveList`,
                    {
                        state: {
                            initialMessage: {
                                title: null,
                                messageLevel: MESSAGE_TYPE.INFORMATION,
                                detail: Messages.IFACC0002.toString(
                                    this.state.facilityName,
                                    (this.state.startDate + ' ' + this.state.startTime),
                                    (this.state.endDate + ' ' + this.state.endTime),
                                ),
                            },
                        },
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async cancelReserve(): Promise<void> {
        let errorLevel: Nullable<MESSAGE_TYPE> = null
        let message: Nullable<string> = null

        if (this.state.reserveCancelWarningFlag === RESERVE_CANCEL_WARNING_OUT_OF_TERM) {
            errorLevel = Messages.WFACA0009.errorLevel
            message = Messages.WFACA0009.toString()
        } else if (this.state.reserveCancelWarningFlag === RESERVE_CANCEL_WARNING_UNAVAILABLE) {
            errorLevel = Messages.WFACA0012.errorLevel
            message = Messages.WFACA0012.toString()
        } else {
            errorLevel = Messages.IFACC0001.errorLevel
            message = Messages.IFACC0001.toString(
                this.state.facilityName,
                (this.state.startDate + ' ' + this.state.startTime),
                (this.state.endDate + ' ' + this.state.endTime),
            )
        }


        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: errorLevel,
                message: message,
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleCancelReserveOkClick,
                    },
                ],
            },
        )
    }

    renderUtilizationTime(): ReactNode {
        if (this.state.stayAvailability === AVAILABLE_OR_NOT.AVAILABLE.value) {
            return (
                <TableRow>
                    <TableRowName>
                        チェックイン～<br/>チェックアウト時間
                    </TableRowName>
                    <TableCell colSpan={2}>
                        {this.state.startDate} {this.state.startTime} ~ {this.state.endDate} {this.state.endTime}
                    </TableCell>
                    <TableRowName>泊数</TableRowName>
                    <TableCell colSpan={2}>
                        {this.state.stayCount}泊
                    </TableCell>
                </TableRow>
            )
        } else {
            return (
                <TableRow>
                    <TableRowName>
                        利用日時
                    </TableRowName>
                    <TableCell colSpan={5}>
                        {this.state.startDate} {this.state.startTime} ~ {this.state.endDate} {this.state.endTime}
                    </TableCell>
                </TableRow>
            )
        }
    }

    renderSelectedUserList(): Nullable<ReactNode> {
        if (this.state.selectedUserList.length === 0) {
            return null
        } else {
            const count: number = this.state.selectedUserList.length
            return (
                <TableRow>
                    <TableRowName>
                        同時入室番号
                    </TableRowName>
                    <TableCell colSpan={5}>
                        {this.state.selectedUserList.map((selectedUser, index) => {
                            return (
                                selectedUser.ridgeName + ' ' +
                                selectedUser.roomNumber + (((count - 1) === index) ? '' : ', ')
                            )
                        })}
                    </TableCell>
                </TableRow>
            )
        }
    }

    // 予約情報テーブル
    renderFacilityReserveTable(): ReactNode {
        const {reserveStatus, cancelReason} = this.state
        let reserveStatusLabel = RESERVATION_STATUS.codeToEnumConstants(this.state.reserveStatus).label
        if (reserveStatus === RESERVATION_STATUS.CANCEL.value && cancelReason !== null) {
            reserveStatusLabel = `${reserveStatusLabel}（${CANCEL_REASON.codeToEnumConstants(cancelReason).label}）`
        }
        return (
            <>
                <ConditionTableFixed>
                    <colgroup>
                        <col width={'20%'}/>
                        <col width={'20%'}/>
                        <col width={''}/>
                        <col width={''}/>
                        <col width={''}/>
                        <col width={''}/>
                    </colgroup>
                    <StyledTableCaption>予約情報</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName>施設名</TableRowName>
                            <TableCell colSpan={5}>
                                {this.state.facilityName}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>予約種別</TableRowName>
                            <TableCell colSpan={2}>
                                {RESERVATION_TYPE.codeToEnumConstants(this.state.reserveType).label}
                            </TableCell>
                            <TableRowName>予約ステータス</TableRowName>
                            <TableCell colSpan={2}>
                                {reserveStatusLabel}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>施設予約区分</TableRowName>
                            <TableCell colSpan={5}>
                                {FACILITY_RESERVATION_CLASSIFICATION.codeToEnumConstants(
                                    this.state.facilityReserveCategory).label}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>予約タイトル</TableRowName>
                            <TableCell colSpan={5}>
                                {this.state.reserveTitle}
                            </TableCell>
                        </TableRow>
                        {this.renderUtilizationTime()}
                        {this.renderSelectedUserList()}
                    </TableBody>
                </ConditionTableFixed>
            </>
        )
    }

    renderFacilityCharge(): ReactNode {
        return (
            <TableRow>
                <TableRowName>施設利用料</TableRowName>
                <TableCellWordBreakAll>
                    {this.state.facilityName}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll
                    colSpan={(this.state.facilityReserveCategory !== FACILITY_RESERVATION_CLASSIFICATION.ADMIN.value) ?
                        1 : 4}>
                    {formatNumberWithCommas(this.state.facilityUseCharge)}円
                </TableCellWordBreakAll>
                {this.state.facilityReserveCategory !== FACILITY_RESERVATION_CLASSIFICATION.ADMIN.value &&
                    <>
                        <TableCell colSpan={3}>
                            {PAYMENT_TYPE.codeToEnumConstants(this.state.paymentMethod).label}
                            {(this.state.paymentMethod === PAYMENT_TYPE.CASH.value) &&
                                <>
                                    （現金支払ステータス：
                                    {CASH_PAYMENT_STATUS.codeToEnumConstants(
                                        this.state.cashPaymentStatus).label}）
                                </>
                            }
                        </TableCell>
                    </>
                }
            </TableRow>
        )
    }

    renderOptionChargeDetail(option: Options): ReactNode {
        return (
            <>
                <TableCellWordBreakAll>
                    {option.optionName}：{option.quantity}{option.unitName}
                </TableCellWordBreakAll>
                <TableCell colSpan={(this.state.facilityReserveCategory !==
                    FACILITY_RESERVATION_CLASSIFICATION.ADMIN.value) ?
                    1 : 4}>
                    {formatNumberWithCommas(option.charge)}円
                </TableCell>
                {this.state.facilityReserveCategory !==
                    FACILITY_RESERVATION_CLASSIFICATION.ADMIN.value &&
                    <>
                        <TableCell colSpan={3}>
                            {PAYMENT_TYPE.codeToEnumConstants(
                                option.paymentType).label}
                            {(option.paymentType === PAYMENT_TYPE.CASH.value) &&
                                <>
                                    （現金支払ステータス：
                                    {CASH_PAYMENT_STATUS.codeToEnumConstants(
                                        option.cashPaymentStatus).label}）
                                </>
                            }
                        </TableCell>
                    </>
                }
            </>
        )
    }

    renderOptionCharge(): Nullable<ReactNode> {
        if (this.state.options.length === 0) {
            return null
        }

        return (
            <>
                {this.state.options.sort(
                    (a, b) => a.optionId - b.optionId).sort(
                    (a, b) => a.sortOrder - b.sortOrder).map((option, index) => {
                    if (index === 0) {
                        return (
                            <TableRow key={index}>
                                <TableRowName rowSpan={this.state.options.length}>オプション</TableRowName>
                                {this.renderOptionChargeDetail(option)}
                            </TableRow>
                        )
                    } else {
                        return (
                            <TableRow key={index}>
                                {this.renderOptionChargeDetail(option)}
                            </TableRow>
                        )
                    }

                })}
            </>
        )
    }

    // 施設利用料テーブル
    renderChargeTable(): ReactNode {
        return (
            <>
                <ConditionTableFixed>
                    <colgroup>
                        <col width={'20%'}/>
                        <col width={'20%'}/>
                        <col width={''}/>
                        <col width={''}/>
                        <col width={''}/>
                        <col width={''}/>
                    </colgroup>
                    <StyledTableCaption>料金情報</StyledTableCaption>
                    <TableBody>
                        {this.renderFacilityCharge()}
                        {this.renderOptionCharge()}
                        <TableRow>
                            <TableRowName>合計金額</TableRowName>
                            <TableCell colSpan={4}>
                                {(this.state.facilityReserveCategory !==
                                    FACILITY_RESERVATION_CLASSIFICATION.ADMIN.value) ?
                                    formatNumberWithCommas(this.state.totalCharge) + '円' : '0円'
                                }
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </ConditionTableFixed>
            </>
        )
    }

    // 利用者情報テーブル
    renderUserInfoTable(): ReactNode {
        return (
            <>
                <ConditionTableFixed>
                    <colgroup>
                        <col width={'20%'}/>
                        <col width={'20%'}/>
                        <col width={''}/>
                        <col width={''}/>
                        <col width={''}/>
                        <col width={''}/>
                    </colgroup>
                    <StyledTableCaption>利用者情報</StyledTableCaption>
                    <TableBody>
                        {this.state.facilityReserveCategory !==
                            FACILITY_RESERVATION_CLASSIFICATION.ADMIN.value &&
                            <TableRow>
                                <TableRowName>部屋番号</TableRowName>
                                <TableCell colSpan={5}>
                                    {this.state.userRidgeName} {this.state.userRoomNumber}
                                </TableCell>
                            </TableRow>
                        }
                        <TableRow>
                            <TableRowName>氏名</TableRowName>
                            <TableCell colSpan={(this.state.facilityReserveCategory !==
                                FACILITY_RESERVATION_CLASSIFICATION.ADMIN.value) ? 1 : 5}>
                                {this.state.userFamilyName} {this.state.userFirstName}
                            </TableCell>
                            {this.state.facilityReserveCategory !==
                                FACILITY_RESERVATION_CLASSIFICATION.ADMIN.value &&
                                <>
                                    <TableRowName>管理者確認用氏名</TableRowName>
                                    <TableCell colSpan={3}>
                                        {this.state.adminFamilyName} {this.state.adminFirstName}
                                    </TableCell>
                                </>
                            }
                        </TableRow>
                        <TableRow>
                            <TableRowName>権限</TableRowName>
                            <TableCell colSpan={5}>
                                {this.state.userRoleName}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </ConditionTableFixed>
            </>
        )
    }

    // 予約者情報テーブル
    renderReserveUserInfoTable(): ReactNode {
        return (
            <>
                <ConditionTableFixed>
                    <colgroup>
                        <col width={'20%'}/>
                        <col width={'20%'}/>
                        <col width={''}/>
                        <col width={''}/>
                        <col width={''}/>
                        <col width={''}/>
                    </colgroup>
                    <StyledTableCaption>予約者情報</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName>予約者氏名</TableRowName>
                            <TableCell colSpan={5}>
                                {this.state.reserveFamilyName} {this.state.reserveFirstName}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>権限</TableRowName>
                            <TableCell colSpan={5}>
                                {this.state.reserveRoleName}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </ConditionTableFixed>
            </>
        )
    }

    rebderLastUpdateUserInfoTable(): ReactNode {
        return (
            <>
                <ConditionTableFixed>
                    <colgroup>
                        <col width={'20%'}/>
                        <col width={'20%'}/>
                        <col width={''}/>
                        <col width={''}/>
                        <col width={''}/>
                        <col width={''}/>
                    </colgroup>
                    <StyledTableCaption>最終更新者情報</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName>最終更新者氏名</TableRowName>
                            <TableCell colSpan={5}>
                                {this.state.lastUpdaterFamilyName} {this.state.lastUpdaterFirstName}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>最終更新日</TableRowName>
                            <TableCell colSpan={5}>
                                {this.state.updateDateTime}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </ConditionTableFixed>
            </>
        )
    }

    renderPageHeaderArea(): Nullable<ReactNode> {
        return (
            <>
                {this.state.reserveStatus !== RESERVATION_STATUS.DIS_ELECTION.value &&
                    this.state.reserveStatus !== RESERVATION_STATUS.CANCEL.value &&
                    <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_RESERVE_LIST}>
                        <PageFotterAreaRight>
                            <DeleteLink
                                onClick={this.cancelReserve}>
                                予約を取り消す
                            </DeleteLink>
                        </PageFotterAreaRight>
                        <PageFotterAreaLeft>
                            <AdminPrimaryButton
                                onClick={this.editFacilityReserveLinkClick}>
                                予約情報を編集する
                            </AdminPrimaryButton>
                        </PageFotterAreaLeft>
                    </AuthorityChecker>
                }
            </>
        )
    }

    renderPageFooterArea(): ReactNode {
        return (
            <>
                <PageFotterAreaLeft>
                    <BackLink
                        onClick={this.backFacilityReserveListLinkClick}
                    >
                        施設予約検索へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                <Box mb={'15px'}>
                    <PageFooterArea>
                        {this.renderPageHeaderArea()}
                    </PageFooterArea>
                </Box>
                <Box mb={'15px'}>
                    <PageMainArea>
                        {this.renderFacilityReserveTable()}
                        {this.renderChargeTable()}
                        {this.renderUserInfoTable()}
                        {this.renderReserveUserInfoTable()}
                        {this.rebderLastUpdateUserInfoTable()}
                    </PageMainArea>
                </Box>
                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

export default createAdminPage(NatAdminFacilityReservePage)

const TableCellWordBreakAll = styled(TableCell)`
  &.MuiTableCell-root {
    word-break: break-all;
  }
`
